import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { requireFeature } from '@/common/utils/requireFeature';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { usePermissions } from '@/common/hooks/usePermissions';
import { techSystemsService } from '@/features/tech-systems/services/tech-systems.service';
import { TechSystemProjectsTab } from '@/features/tech-systems/components/TechSystemProjectsTab';
import {
  canAccessProjectsHub,
  canAccessTechSystemsModule,
  resolveProjectsHubCanEdit,
} from '@/features/tech-systems/utils/projects-access';

export const Route = createFileRoute('/_authenticated/tech-systems/projects/$id')({
  beforeLoad: () => requireFeature('TECH_SYSTEMS'),
  component: TechSystemProjectsDetailPage,
});

function TechSystemProjectsDetailPage() {
  const { id } = Route.useParams();
  const { hasPermission, user } = usePermissions();

  const hasProjectsHubAccess = canAccessProjectsHub(hasPermission);
  const hasModuleAccess = canAccessTechSystemsModule(hasPermission);

  const { data: system, isLoading, isError } = useQuery({
    queryKey: ['tech-system', id],
    queryFn: () => techSystemsService.get(id),
    enabled: hasProjectsHubAccess && hasModuleAccess,
  });

  const memberLevel = system?.members?.find((member) => member.userId === user?.id)
    ?.accessLevel;
  const canEdit = resolveProjectsHubCanEdit({ hasPermission, memberLevel });

  if (!hasProjectsHubAccess) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-2xl font-semibold">Projeto do sistema</h1>
        <p className="text-sm text-muted-foreground">
          Você não tem permissão para acessar a gestão de projetos dos Sistemas
          TEC.
        </p>
      </div>
    );
  }

  if (!hasModuleAccess) {
    return (
      <div className="space-y-4 p-6">
        <h1 className="text-2xl font-semibold">Projeto do sistema</h1>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Você possui acesso à gestão de projetos, mas não tem permissão no
            módulo Sistemas TEC. Solicite acesso a um gestor.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 text-muted-foreground">Carregando projeto...</div>
    );
  }

  if (isError || !system) {
    return (
      <div className="space-y-4 p-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tech-systems/projects">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Link>
        </Button>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Não foi possível carregar este sistema ou você não tem acesso a ele.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tech-systems/projects">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para projetos
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">{system.name}</h1>
          <p className="text-sm text-muted-foreground">
            Cronograma e tarefas do projeto
          </p>
        </div>
      </div>

      <TechSystemProjectsTab techSystemId={id} canEdit={canEdit} />
    </div>
  );
}
